import { useSnackbar } from "notistack";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { sendGaEvent } from "../main/sendGaEvent";
import { showMessage } from "../state/app/appActions";
import { getNovosPedidosPendentes } from "../state/order/actions";
import { useRemoteConfig } from "../state/remoteConfig/RemoteConfigContext";
import { remoteConfigTypes } from "../state/remoteConfig/types";
import {
	prepararAlertaSonoro,
	RESULTADO_ALERTA,
	tocarAlertaPedidoAtrasado,
} from "../utils/alerta-sonoro";
import AlertaPedidosPendentes from "../views/components/GlobalAlerts/AlertaPedidosPendentes";

const MINUTOS_ATRASO_FALLBACK = 15;
const MINUTOS_INTERVALO_FALLBACK = 5;

const SNACKBAR_KEY = "alerta-pedidos-pendentes";
const SNACKBAR_KEY_SOM_BLOQUEADO = "alerta-sonoro-bloqueado";

const estaAtrasado = (pedido, limiteMs) =>
	!!pedido.dataInclusao &&
	Date.now() - new Date(pedido.dataInclusao).getTime() > limiteMs;

const temPedidoAtrasado = (pedidos, limiteMs) =>
	pedidos.some((pedido) => estaAtrasado(pedido, limiteMs));

const MonitorPedidosPendentes = () => {
	const dispatch = useDispatch();
	const { enqueueSnackbar, closeSnackbar } = useSnackbar();
	const { getGlobalConfig } = useRemoteConfig();
	const loja = useSelector((state) => state.auth.loja);

	const execucaoRef = React.useRef(0);

	const getMinutosConfig = (chave, fallback) =>
		getGlobalConfig ? getGlobalConfig(chave, fallback) : fallback;

	const alertaHabilitado = getGlobalConfig
		? getGlobalConfig(
				remoteConfigTypes.GLOBAL_CONFIG.ALERTA_PEDIDO_ATRASADO_HABILITADO,
				true,
			) !== false
		: true;

	const minutosAtraso = getMinutosConfig(
		remoteConfigTypes.GLOBAL_CONFIG.MINUTOS_PEDIDO_ATRASADO,
		MINUTOS_ATRASO_FALLBACK,
	);

	const minutosIntervalo = getMinutosConfig(
		remoteConfigTypes.GLOBAL_CONFIG.MINUTOS_INTERVALO_BUSCA_PEDIDO_ATRASADO,
		MINUTOS_INTERVALO_FALLBACK,
	);

	const avisarAlertaSonoroBloqueado = React.useCallback(() => {
		dispatch(
			showMessage(
				"Não foi possível tocar o alerta sonoro. Favor habilitar o som no seu navegador.",
				"error",
				{ key: SNACKBAR_KEY_SOM_BLOQUEADO, preventDuplicate: true },
			),
		);
	}, [dispatch]);

	const exibirAlerta = React.useCallback(() => {
		sendGaEvent("delayed_order", "global", {
			minutes_threshold: minutosAtraso,
			minutes_interval: minutosIntervalo,
		});

		tocarAlertaPedidoAtrasado().then((resultado) => {
			if (resultado === RESULTADO_ALERTA.ERRO) {
				avisarAlertaSonoroBloqueado();
			}
		});

		enqueueSnackbar("Atenção: pedidos aguardando confirmação", {
			key: SNACKBAR_KEY,
			persist: true,
			anchorOrigin: { vertical: "bottom", horizontal: "center" },
			content: (key) => (
				<AlertaPedidosPendentes id={key} minutos={minutosAtraso} />
			),
		});
	}, [
		enqueueSnackbar,
		minutosAtraso,
		minutosIntervalo,
		avisarAlertaSonoroBloqueado,
	]);

	const verificarPedidosPendentes = React.useCallback(async () => {
		const execucao = execucaoRef.current;
		const pedidos = await dispatch(getNovosPedidosPendentes());

		if (execucao !== execucaoRef.current) {
			return;
		}

		const temAtrasado = temPedidoAtrasado(pedidos, minutosAtraso * 60 * 1000);

		closeSnackbar(SNACKBAR_KEY);

		if (temAtrasado) {
			exibirAlerta();
		}
	}, [dispatch, minutosAtraso, closeSnackbar, exibirAlerta]);

	useEffect(() => {
		if (!alertaHabilitado) {
			return undefined;
		}

		return prepararAlertaSonoro();
	}, [alertaHabilitado]);

	useEffect(() => {
		if (!loja || !alertaHabilitado) {
			return undefined;
		}

		execucaoRef.current += 1;
		verificarPedidosPendentes();
		const intervalo = setInterval(
			verificarPedidosPendentes,
			minutosIntervalo * 60 * 1000,
		);

		return () => {
			execucaoRef.current += 1;
			clearInterval(intervalo);
			closeSnackbar(SNACKBAR_KEY);
		};
	}, [
		loja,
		alertaHabilitado,
		minutosIntervalo,
		verificarPedidosPendentes,
		closeSnackbar,
	]);

	return null;
};

export default MonitorPedidosPendentes;
